import h from 'hyperscript';

import { createAnchorButton } from 'ui/button/button';
import alertStyles from 'ui/alert/alert.module.css' assert { type: 'css' };
import { FormElement } from 'ui/form-element/form-element';
import { validationMessage } from 'ui/form-validation-messages';
import type { ValidationMessageType } from 'ui/form-validation-messages';
import { type Content, isContent } from 'ui/content';

import { addStyleClass } from 'cadenza/utils/add-style-class';
import { array } from 'cadenza/utils/array-util';
import { ariaTrueFalse } from 'cadenza/utils/aria';
import { type EventKey, focusFirst, on, unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';
import { logger } from 'cadenza/utils/logging';

import styles from './form-group.module.css' assert { type: 'css' };
import './form-group.css';
import i18n from './form-group.properties';

const COMPONENT_NAME = 'form-group';
const SIZES = ['xs', 's', 'l'];

type FormGroupSize = (typeof SIZES)[number];

/**
 * @event invalid - When the control becomes invalid.
 */
// We need this type, because we do a lot of duck-typing in here
// to support native form controls as well as custom ones.
export type Control = HTMLElement &
  Partial<
    Pick<
      HTMLInputElement,
      | 'value'
      | 'defaultValue'
      | 'checked'
      | 'defaultChecked'
      | 'disabled'
      | 'required'
      | 'readOnly'
      | 'willValidate'
      | 'min'
      | 'max'
      | 'checkValidity'
      | 'validationMessage'
      | 'validity'
      | 'dataset'
    >
  >;

/**
 * The object of type ValidationMessages contains messages to be displayed for corresponding violations.
 * These messages are used to overwrite the user agent standard message.
 * Except for `customError`, the property names correspond to violations defined on type
 * [ValidityStateFlags](https://html.spec.whatwg.org/multipage/custom-elements.html#validitystateflags).
 *
 * Additionally, we have the convenience type `outOfRange`.
 * This message is used when the control has both min and max boundaries.
 *
 * A hint message is shown if a control is valid and a `validationHint` is defined.
 *
 * The `validationMessage` is shown to user if the component is invalid
 * but the validity state does not match any state of the `ValidityStateFlags`.
 */
export type ValidationMessages = Partial<
  Record<`${ValidationMessageType}Message` | 'validationHint' | 'validationMessage', string>
>;

export function setValidationMessages(control: Control, messages?: ValidationMessages): void;
export function setValidationMessages(formGroup: FormGroup, messages?: ValidationMessages): void;
export function setValidationMessages(controlOrFormGroup: Control | FormGroup, messages?: ValidationMessages) {
  if (controlOrFormGroup instanceof FormGroup) {
    controlOrFormGroup = controlOrFormGroup.control ?? controlOrFormGroup;
  }
  for (let [attribute, value] of Object.entries(messages ?? {})) {
    if (value) {
      controlOrFormGroup.dataset[attribute] = value;
    }
  }
}

/**
 * Groups a form control and its label.
 *
 * The control is either a native control (input, select, textarea) or marked with the
 * .form-group-control CSS class.
 */
export class FormGroup extends HTMLElement {
  #observer = new MutationObserver(() => this._update());
  override shadowRoot!: ShadowRoot;

  _hint?: HTMLElement;
  _alert?: HTMLElement;
  #eventKeys: EventKey[] = [];
  #isInitialized = false;

  constructor() {
    super();
    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.adoptedStyleSheets = [styles, alertStyles];
  }

  get label() {
    return this.querySelector<HTMLLabelElement>(':scope > label');
  }

  get control() {
    let control = null;
    ['.form-group-control', 'input', 'select', 'textarea', '.form-control'].some((selector) => {
      // This loop is different from a single querySelector(), because it tries the selectors
      // in order. Therefore: do not alter the order!

      control = this.querySelector(selector);
      return control != null;
    });
    return control as Control | null;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);

    // Slots should be created synchronously (i.e. their creation should not be deferred until the
    // stack is clear). If not it can happen that slotted child components cannot be rendered properly.
    const controlContainer = h('slot.control-container');
    this.shadowRoot.append(controlContainer);

    if (
      window.Disy.debug &&
      this.parentNode instanceof HTMLElement && // DocumentFragment when rendered via Lit template
      this.parentNode.closest('.form-group')
    ) {
      logger.warn(
        'a form-group (see below) has another form-group as an ancestor. ' +
          ' Nesting form-groups might cause issues in form validation and accessibility',
        this,
      );
    }

    // setTimeout is necessary because we don't want to execute this code
    // before the form-group is appended to the light DOM. It is necessary because
    // there are cases where input-control is a child of a child and will otherwise not be found.
    setTimeout(() => {
      const { label, control } = this;

      if (label && control) {
        label.classList.toggle('d-form-check', label.contains(control));

        if (this.resettable) {
          const resetButton = this._createResetButton(control);
          resetButton.hidden =
            control instanceof HTMLSelectElement ?
              control.value === defaultSelectValue(control)
            : control.value === control.defaultValue && control.checked === control.defaultChecked;
          this.#eventKeys.push(
            on(control, 'change', () => {
              resetButton.hidden = false;
            }),
          );
          this.append(resetButton);
        }
      }

      /*
       * The form group component used to be the combination of control and its label.
       * Advanced features like links and buttons next to the label made the layout more complex
       * and we use the Shadow DOM to encapsulate this complexity.
       *
       * We do not want to mess up the layout of legacy (pre-Shadow-DOM) form groups,
       * so we use the presence of slot attributes as a switch to enable the new layout.
       */
      const usesSlots = this.#usesSlots;

      if (label && usesSlots) {
        this.shadowRoot.prepend(
          h('.header', [h('slot.label-container', { name: 'label' }), h('slot.label-aside', { name: 'label-aside' })]),
        );
        label.slot = 'label';
      }

      if (!this.size) {
        this.classList.toggle(sizeClass('flex'), !this.#usesSlots);
      }

      if (control) {
        controlContainer.classList.add('has-control');
        control.id ||= uniqueId();

        // connect control and label
        if (!this.#isInitialized) {
          // sometimes setTimeout is executed twice, this is to prevent double alerts => to be investigated, why this is happening
          if (label) {
            label.htmlFor ||= control.id;

            if (label.htmlFor === control.id && !control.hasAttribute('aria-labelledby')) {
              label.id ||= control.id + '-label';
              control.setAttribute('aria-labelledby', label.id);
            }

            this.#eventKeys.push(on(label, 'click', () => focusFirst(control)));

            /*
             * Certain password managers (e.g. 1password) ignore the autocomplete=off on the form
             * and still offer auto-completion on inputs that are labelled with "Name".
             */
            if (
              control instanceof HTMLInputElement &&
              control.type === 'text' &&
              !control.hasAttribute('autocomplete') &&
              control.form?.autocomplete === 'off' &&
              label.textContent?.startsWith('Name')
            ) {
              control.autocomplete = 'off';
            }
          }

          // setup classes
          this.classList.toggle(
            'is-readonly',
            ('readOnly' in control && control.readOnly) || ariaTrueFalse(control, 'readonly'),
          );

          /*
           * Changing the style of the control (by adding the form-control CSS class) was a bad idea.
           * For backwards-compatibility we do it only for native controls that are known to work with .form-control.
           */
          if (
            control.matches(
              'input:not([type]),input[type=text],input[type=number],input[type=password],select,textarea',
            )
          ) {
            control.classList.add('form-control');
          }

          /*
           * Add validation alert and hint elements.
           * The alert is shown if the control is invalid, the hint otherwise.
           */
          this._alert = createAlert(control.id);
          this._hint = createValidationHint(control.id);
          this.shadowRoot.append(this._alert, this._hint);

          // aria-errormessage is not well-supported, so we use aria-describedby also for the alert:
          // https://a11ysupport.io/tech/aria/aria-errormessage_attribute
          control.setAttribute('aria-describedby', `${this._alert.id} ${this._hint.id}`);
          this.#isInitialized = true;
        }

        // observe control.disabled
        this._update();
        this.#observer.observe(control, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'data-validation-hint', 'data-validation-message', 'required'],
        });

        // observe control.validity
        const validationControl = control.checkValidity ? control : control.querySelector<Control>('input'); // use nested <input> as fallback
        if (validationControl) {
          this.#eventKeys.push(on(validationControl, 'invalid', (event) => this._onInvalid(event)));
        }

        if (this.dataset.validationMessage || this.dataset.validationHint) {
          logger.warn(
            'The data-validation-message and data-validation-hint attributes on the <form-group> are ignored, ' +
              'because there is a control from which these messages are resolved.',
            this,
          );
        }
      } else if (this.dataset.validationMessage) {
        const alert = createAlert();
        alert.textContent = this.dataset.validationMessage;
        this.shadowRoot.append(alert);
      } else if (this.dataset.validationHint) {
        const hint = createValidationHint();
        hint.textContent = this.dataset.validationHint;
        this.shadowRoot.append(hint);
      }

      if (this.dataset.validateOnConnect) {
        this.checkValidity();
      }
    });
  }

  #updateRequiredState(control: Control) {
    this.classList.toggle('is-required', control.required || ariaTrueFalse(control, 'required'));
  }

  _createResetButton(control: Control) {
    const resetButton = createAnchorButton(i18n('reset'), {
      slot: 'label',
      hidden: true,
      onclick: (event: Event) => {
        // The click should not be handled again by ancestors.
        // For example a readonly inline-editing context would otherwise switch to editing mode.
        event.stopPropagation();
        reset(control);
        this.dispatchEvent(new CustomEvent('reset'));
        resetButton.hidden = true;
      },
    });
    return resetButton;
  }

  // Since we have an (invalid) control, we know that the alert is defined.
  _onInvalid(event: Event) {
    event.preventDefault();
    this._alert!.textContent = this.#validationMessage;
    this.classList.add('is-invalid');
  }

  disconnectedCallback() {
    unByKey(this.#eventKeys);
    this.#eventKeys = [];
    this.resetInvalid();
    this.#observer.disconnect();
    this.shadowRoot.innerHTML = '';
    this._alert = undefined;
    this._hint = undefined;
    this.#isInitialized = false;
  }

  // This method is called only when we have a control.
  _update() {
    const control = this.control!; // In this method, we know that we have a control.
    this.classList.toggle('is-disabled', control.disabled || ariaTrueFalse(control, 'disabled'));
    this.#updateRequiredState(control);
    this.resetInvalid();
    this._hint!.textContent = this.control!.dataset.validationHint ?? '';
  }

  resetInvalid() {
    this.classList.remove('is-invalid');

    if (this._alert) {
      this._alert.textContent = '';
    }
  }

  checkValidity() {
    return this.control?.checkValidity?.() ?? true;
  }

  get #validationMessage() {
    const control = this.control!;
    if (control instanceof FormElement) {
      return control.validationMessage;
    }
    const validity = control.validity;
    const defaultMessage = control.dataset.validationMessage ?? '';
    // Custom elements may not have validity property
    if (validity) {
      if (validity.customError) {
        return control.validationMessage ?? ''; // custom error should win over other errors
      } else if (validity.valueMissing) {
        return this.#resolveMessage('valueMissing');
      } else if (validity.badInput) {
        // Defaults to default message because no specific message is defined for badInput
        return control.dataset.badInputMessage || defaultMessage;
      } else if (validity.rangeOverflow || validity.rangeUnderflow) {
        const { min: minString, max: maxString } = control;
        const min = Number(minString);
        const max = Number(maxString);
        const useOutOfRangeMessage =
          control.dataset.outOfRangeMessage != null ||
          !(control.dataset.rangeOverflowMessage || control.dataset.rangeUnderflowMessage);
        if (minString && maxString && useOutOfRangeMessage) {
          return this.#resolveMessage('outOfRange', { min, max });
        } else if (validity.rangeUnderflow) {
          return this.#resolveMessage('rangeUnderflow', { limit: min });
        } else {
          return this.#resolveMessage('rangeOverflow', { limit: max });
        }
      } else if (validity.tooLong) {
        return this.#resolveMessage('tooLong');
      } else if (validity.tooShort) {
        return this.#resolveMessage('tooShort');
      } else if (validity.patternMismatch) {
        return this.#resolveMessage('patternMismatch');
      }
    }
    return defaultMessage;
  }

  #resolveMessage(flag: ValidationMessageType, options?: Record<string, unknown>) {
    // Validation message is only resolved if there's an invalid control.
    return this.control!.dataset[`${flag}Message`] ?? validationMessage(flag, options);
  }

  set resettable(value: boolean) {
    this.toggleAttribute('resettable', value);
  }

  get resettable() {
    return this.hasAttribute('resettable');
  }

  set size(value: FormGroupSize | undefined) {
    SIZES.forEach((size) => this.classList.toggle(sizeClass(size), size === value));
    this.classList.toggle(sizeClass('flex'), value === undefined && !this.#usesSlots);
  }

  get size() {
    return SIZES.find((size) => this.classList.contains(sizeClass(size)));
  }

  get #usesSlots() {
    return this.querySelector(':scope > [slot]') != null;
  }
}

customElements.define(COMPONENT_NAME, FormGroup);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: FormGroup;
  }
}

function sizeClass(size: FormGroupSize | 'flex') {
  return `${COMPONENT_NAME}-${size}`;
}

function reset(control: Control) {
  if (control) {
    if (control instanceof FormElement) {
      control.reset();
    } else if (control instanceof HTMLSelectElement) {
      control.value = defaultSelectValue(control);
    } else if (control instanceof HTMLInputElement && control.type === 'checkbox') {
      control.checked = control.defaultChecked;
    } else if (typeof control.defaultValue === 'string') {
      control.value = control.defaultValue;
    }
  }
}

function defaultSelectValue(select: HTMLSelectElement) {
  return select.querySelector<HTMLOptionElement>('option[selected]')?.value ?? select.value;
}

export function allFormGroupsValid(parent: Element) {
  const invalidFormGroups = parent.querySelectorAll(`${COMPONENT_NAME}.is-invalid`);
  invalidFormGroups.forEach((el) => {
    const formGroup = el as FormGroup;
    if (formGroup.checkValidity()) {
      formGroup.resetInvalid();
    }
  });

  const invalidElements = parent.querySelectorAll(':invalid');
  if (invalidElements.length) {
    // trigger display of validation error message to the user
    (invalidElements[0].closest('form') as HTMLFormElement).checkValidity();
    // we have custom validation messages in our form groups (default above is fallback)
    invalidElements.forEach((el) => (el.closest(COMPONENT_NAME) as FormGroup)?.checkValidity());
    return false;
  }
  return true;
}

function createValidationHint(id?: string): HTMLElement {
  return h('p.validation-hint', { ...(id && { id: `${id}-hint` }), attrs: { role: 'note' } });
}

function createAlert(id?: string): HTMLElement {
  return h('p.invalid-feedback.d-alert.d-alert-xs.d-alert-error', {
    ...(id && { id: `${id}-alert` }),
    attrs: { role: 'alert' },
  });
}

interface FormGroupOptions {
  size?: FormGroupSize;
  resettable?: boolean;
  validationMessage?: string;
  validationHint?: string;
  styleClass?: string | string[];
}

export function createFormGroup(label: string, content: Content, options?: FormGroupOptions): FormGroup;
export function createFormGroup(content: Content, options?: FormGroupOptions): FormGroup;
export function createFormGroup(
  labelOrContent: Content,
  contentOrOptions?: Content | FormGroupOptions,
  options: FormGroupOptions = {},
) {
  let label, content;
  if (isContent(contentOrOptions)) {
    label = labelOrContent;
    content = contentOrOptions;
  } else {
    content = labelOrContent;
    options = contentOrOptions ?? {};
  }
  const { size, resettable, validationMessage, validationHint, styleClass } = options;
  const formGroup = h<FormGroup>(
    'form-group',
    {
      size,
      resettable: resettable ?? false,
    },
    [label && h('label', { slot: 'label' }, label), ...array(content)],
  );
  if (validationMessage) {
    formGroup.dataset.validationMessage = validationMessage;
  }
  if (validationHint) {
    formGroup.dataset.validationHint = validationHint;
  }
  addStyleClass(formGroup, styleClass);
  return formGroup;
}
